import React, { useState, useMemo } from 'react';

import Button from '../../../components/ui/Button';
import { cn } from '../../../utils/cn';

const CalendarView = ({ permits, onSelectPermit }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('month'); // 'month' or 'week'

  const getDaysInMonth = (date) => {
    const year = date?.getFullYear();
    const month = date?.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay?.getDate();
    const startingDayOfWeek = firstDay?.getDay();
    
    return { daysInMonth, startingDayOfWeek, year, month };
  };

  const getWeekDays = (date) => {
    const startOfWeek = new Date(date);
    startOfWeek?.setDate(date?.getDate() - date?.getDay());
    
    const days = [];
    for (let i = 0; i < 7; i++) {
      let day = new Date(startOfWeek);
      day?.setDate(startOfWeek?.getDate() + i);
      days?.push(day);
    }
    return days;
  };

  const getPermitsForDate = (date) => {
    return permits?.filter(permit => {
      const permitDate = new Date(permit?.deadline);
      return permitDate?.toDateString() === date?.toDateString();
    });
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate?.setMonth(currentDate?.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const navigateWeek = (direction) => {
    const newDate = new Date(currentDate);
    newDate?.setDate(currentDate?.getDate() + (direction * 7));
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const formatMonthYear = (date) => {
    return date?.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const formatWeekRange = (days) => {
    const start = days?.[0];
    const end = days?.[6];
    return `${start?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
  };

  const getStatusColor = (status) => {
    const colors = {
      'submission': 'bg-blue-500',
      'under review': 'bg-warning',
      'approved': 'bg-success',
      'rejected': 'bg-destructive'
    };
    return colors?.[status] || 'bg-gray-500';
  };

  const isToday = (date) => {
    const today = new Date();
    return date?.toDateString() === today?.toDateString();
  };

  const renderMonthView = () => {
    const { daysInMonth, startingDayOfWeek, year, month } = getDaysInMonth(currentDate);
    const days = [];
    
    // Empty cells for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days?.push(<div key={`empty-${i}`} className="min-h-24 p-2 bg-muted/30" />);
    }
    
    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const dayPermits = getPermitsForDate(date);
      const today = isToday(date);
      
      days?.push(
        <div
          key={day}
          className={cn(
            "min-h-24 p-2 border border-border hover:bg-muted/50 transition-colors",
            today && "bg-primary/5 border-primary"
          )}
        >
          <div className={cn(
            "text-sm font-medium mb-2",
            today ? "text-primary" : "text-foreground"
          )}>
            {day}
          </div>
          <div className="space-y-1">
            {dayPermits?.slice(0, 3)?.map((permit) => (
              <button
                key={permit?.id}
                onClick={() => onSelectPermit(permit)}
                className={cn(
                  "w-full text-left px-2 py-1 rounded text-xs font-medium text-white truncate hover:opacity-80 transition-opacity",
                  getStatusColor(permit?.status)
                )}
                title={permit?.projectName}
              >
                {permit?.projectName}
              </button>
            ))}
            {dayPermits?.length > 3 && (
              <div className="text-xs text-muted-foreground px-2">
                +{dayPermits?.length - 3} more
              </div>
            )}
          </div>
        </div>
      );
    }
    
    return days;
  };

  const renderWeekView = () => {
    const weekDays = getWeekDays(currentDate);
    
    return weekDays?.map((date, index) => {
      const dayPermits = getPermitsForDate(date);
      const today = isToday(date);
      
      return (
        <div key={index} className="flex-1 border-r border-border last:border-r-0">
          <div className={cn(
            "p-3 border-b border-border text-center",
            today && "bg-primary/5"
          )}>
            <div className="text-xs text-muted-foreground mb-1">
              {date?.toLocaleDateString('en-US', { weekday: 'short' })}
            </div>
            <div className={cn(
              "text-lg font-semibold",
              today ? "text-primary" : "text-foreground"
            )}>
              {date?.getDate()}
            </div>
          </div>
          <div className="p-2 space-y-2 min-h-96">
            {dayPermits?.map((permit) => (
              <button
                key={permit?.id}
                onClick={() => onSelectPermit(permit)}
                className={cn(
                  "w-full text-left p-2 rounded-lg text-xs font-medium text-white hover:opacity-80 transition-opacity",
                  getStatusColor(permit?.status)
                )}
              >
                <div className="font-semibold mb-1 truncate">{permit?.projectName}</div>
                <div className="text-xs opacity-90">{permit?.permitType}</div>
              </button>
            ))}
          </div>
        </div>
      );
    });
  };

  return (
    <div className="bg-card rounded-xl shadow-sm border border-border overflow-hidden">
      {/* Calendar Header */}
      <div className="p-4 md:p-6 border-b border-border">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              iconName="ChevronLeft"
              onClick={() => viewMode === 'month' ? navigateMonth(-1) : navigateWeek(-1)}
            />
            <div className="text-center min-w-48">
              <h3 className="font-semibold text-lg text-foreground">
                {viewMode === 'month' ? formatMonthYear(currentDate) : formatWeekRange(getWeekDays(currentDate))}
              </h3>
            </div>
            <Button
              variant="outline"
              size="sm"
              iconName="ChevronRight"
              onClick={() => viewMode === 'month' ? navigateMonth(1) : navigateWeek(1)}
            />
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={goToToday}
            >
              Today
            </Button>
            <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
              <Button
                variant={viewMode === 'month' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('month')}
              >
                Month
              </Button>
              <Button
                variant={viewMode === 'week' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('week')}
              >
                Week
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Calendar Grid */}
      {viewMode === 'month' ? (
        <div>
          {/* Weekday Headers */}
          <div className="grid grid-cols-7 border-b border-border">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']?.map((day) => (
              <div key={day} className="p-3 text-center text-sm font-semibold text-muted-foreground border-r border-border last:border-r-0">
                {day}
              </div>
            ))}
          </div>
          {/* Calendar Days */}
          <div className="grid grid-cols-7">
            {renderMonthView()}
          </div>
        </div>
      ) : (
        <div className="flex">
          {renderWeekView()}
        </div>
      )}

      {/* Legend */}
      <div className="p-4 border-t border-border">
        <div className="flex flex-wrap items-center gap-4">
          <span className="text-sm font-medium text-muted-foreground">Status:</span>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-blue-500" />
            <span className="text-xs text-muted-foreground">Submission</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-warning" />
            <span className="text-xs text-muted-foreground">Under Review</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-success" />
            <span className="text-xs text-muted-foreground">Approved</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-destructive" />
            <span className="text-xs text-muted-foreground">Rejected</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalendarView;